// SkinExpert/src/app/services/StripeService.ts
import AuthService from "./AuthService";

const authService = new AuthService();
const IDENTITY_API = process.env.REACT_APP_AUTHORITY;

export interface SubscriptionInfo {
  hasSubscription: boolean;
  status?: string;
  planType?: string;
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd?: boolean;
  trialEnd?: Date;
}

class StripeService {
  private async getHeaders(): Promise<HeadersInit> {
    const token = await authService.getAccessToken();
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  async createCheckoutSession(
    priceId: string,
    successUrl?: string,
    cancelUrl?: string
  ): Promise<{ sessionId: string; url: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/stripe/create-checkout-session`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        priceId,
        successUrl,
        cancelUrl,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create checkout session");
    }

    return response.json();
  }

  async getSubscription(): Promise<SubscriptionInfo> {
    try {
      const headers = await this.getHeaders();
      const url = `${IDENTITY_API}/api/stripe/subscription`;
      console.log("Fetching subscription from URL:", url);
      console.log("Headers:", headers);
      
      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Subscription fetch error:", response.status, errorText);
        throw new Error(`Failed to fetch subscription: ${response.status}`);
      }

      const responseText = await response.text();
      console.log("Raw response text:", responseText);
      
      const data = JSON.parse(responseText);
      console.log("Parsed subscription data:", data);
      return data;
    } catch (error: any) {
      console.error("Error in getSubscription:", error);
      throw error;
    }
  }

  async cancelSubscription(): Promise<{ message: string; cancelAt: Date }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/stripe/cancel-subscription`, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to cancel subscription");
    }

    return response.json();
  }

  async createCustomerPortalSession(returnUrl?: string): Promise<{ url: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/stripe/customer-portal`, {
      method: "POST",
      headers,
      body: JSON.stringify({ returnUrl }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create customer portal session");
    }

    return response.json();
  }

  async syncSubscription(): Promise<{ message: string; planType: string; status: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${IDENTITY_API}/api/stripe/sync-subscription`, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to sync subscription");
    }

    return response.json();
  }
}

export default new StripeService();
